import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import Typography from "@mui/material/Typography";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { api, type TenantProfileRun, type TenantProfileRunStage } from "../../api/client.ts";
import { keys } from "../../api/queryKeys.ts";
import { isJobActive } from "../../utils/status.ts";

const STAGE_LABELS: Record<TenantProfileRunStage, string> = {
  queued: "Getting ready",
  looking_up: "Looking up the company",
  researching: "Reading the company's website",
  fetching_logo: "Finding the company's icon",
  saving: "Saving the profile",
  designing: "Designing workflows",
  done: "Done",
};

/** Whether the run has put the profile on the tenant (it may still be designing workflows). */
export function profileReady(run: TenantProfileRun | null | undefined): boolean {
  return !!run && (run.status === "completed" || run.stage === "designing");
}

/** The tenant's newest profile research run; each stage it reaches is pushed as a Tenant event. */
export function useTenantProfileRun(tenantId: string | null | undefined) {
  return useQuery({
    queryKey: keys.tenant.profileRun(tenantId ?? ""),
    queryFn: () => api.getLatestTenantProfileResearch(tenantId!),
    enabled: !!tenantId,
    meta: { live: true },
  });
}

function useElapsed(since: string | null | undefined, running: boolean): string | null {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!running) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [running]);
  if (!since) return null;
  const seconds = Math.max(0, Math.floor((now - new Date(since).getTime()) / 1000));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}

/**
 * The bar, the stage and the agent's current step while a run works; its error after.
 * Without a run yet (just started) it shows the bar waiting.
 */
export default function TenantResearchProgress({ run }: { run: TenantProfileRun | undefined }) {
  const active = !run || isJobActive(run.status);
  const elapsed = useElapsed(run?.started_at ?? run?.created_at, active);

  if (!run) {
    return (
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
          {STAGE_LABELS.queued}
        </Typography>
        <LinearProgress sx={{ height: 8, borderRadius: 1 }} />
      </Box>
    );
  }

  if (run.status === "failed") {
    return <Alert severity="error">{run.error ?? "The research failed."}</Alert>;
  }

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mb: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>
          {STAGE_LABELS[run.stage] ?? "Working"}
        </Typography>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {run.progress}%{active && elapsed ? ` · ${elapsed}` : ""}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={run.progress}
        sx={{ height: 8, borderRadius: 1 }}
      />
      <Typography
        variant="caption"
        noWrap
        component="div"
        title={run.activity ?? undefined}
        sx={{ color: "text.secondary", mt: 1, minHeight: 20 }}
      >
        {active ? (run.activity ?? "Working…") : run.activity}
      </Typography>
    </Box>
  );
}
